import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useStopTurn } from '../../hooks/useStopTurn';
import { MentionEditor, type MentionEditorHandle } from './MentionEditor';
import { AttachmentChips, RejectedFilesNote } from './AttachmentChips';
import { ComposerActions } from './ComposerActions';
import { SlashCommandPopover } from './SlashCommandPopover';
import { useComposerAttachments } from './useComposerAttachments';
import { useSessionDraft } from './useSessionDraft';
import { useSlashCommands } from './useSlashCommands';
import type { ComposerProps } from './Composer.types';

export type { ComposerProps };

const DISCLAIMER = 'Kula AI can make mistakes. Please double-check responses.';

export const Composer = ({
  placeholder = 'Ask Kula...',
  multiline = false,
  isAwaiting = false,
  autoFocus = false,
  onSubmit,
  onKeyType,
  onContentChange,
}: ComposerProps) => {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [plainText, setPlainText] = useState('');
  const { stop: stopTurn, stopping } = useStopTurn();
  const {
    files,
    rejectedNames,
    inputRef,
    openPicker,
    handleFilesPicked,
    removeFile,
    clearFiles,
  } = useComposerAttachments();
  const { initialDraft, scheduleSave, clearDraft } = useSessionDraft(editorRef);

  const applySlashCommand = useCallback((text: string) => {
    editorRef.current?.setText(text);
  }, []);
  const slash = useSlashCommands({ plainText, onComplete: applySlashCommand });

  const handleEditorChange = useCallback(
    (text: string) => {
      setPlainText(text);
      scheduleSave();
    },
    [scheduleSave]
  );

  const trimmed = plainText.trim();
  // Allow send when there's text OR attachments — uploading just a file
  // (no prompt) is a valid intent the caller can interpret.
  const canSend = trimmed.length > 0 || files.length > 0;

  // Notify the caller when content presence flips (empty ↔ has text/files).
  useEffect(() => {
    onContentChange?.(canSend);
  }, [canSend, onContentChange]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    // Attachments can't be queued behind a running turn — the File handles
    // don't survive; text-only submits queue via useChatSubmit instead.
    if (isAwaiting && files.length > 0) return;
    const serialized = editorRef.current?.getSerialized().trim() ?? '';
    const attached = files;
    editorRef.current?.clear();
    setPlainText('');
    clearFiles();
    clearDraft();
    onSubmit?.(serialized, attached);
  }, [canSend, clearDraft, clearFiles, files, isAwaiting, onSubmit]);

  // Submit on Enter; Shift+Enter inserts a newline (multiline only).
  // While a turn is running, Enter still submits — useChatSubmit queues the
  // message and sends it when the reply finishes (ChatGPT-style), so a fast
  // follow-up thought is never swallowed.
  //
  // The external `onKeyType` runs first so it can `preventDefault` to
  // suppress the built-in submit-on-Enter when the caller wants to
  // intercept the keystroke.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      onKeyType?.(event);
      if (event.defaultPrevented) return;
      // The slash popover owns its navigation keys while it's open.
      if (slash.handleNavKey(event)) return;
      if (event.key !== 'Enter') return;
      if (multiline && event.shiftKey) return;
      event.preventDefault();
      if (slash.completeIfPartial()) return;
      handleSend();
    },
    [handleSend, multiline, onKeyType, slash]
  );

  const slashPopover = slash.open ? (
    <SlashCommandPopover
      matches={slash.matches}
      activeIndex={slash.activeIndex}
      onHover={slash.setIndex}
      onSelect={slash.apply}
    />
  ) : null;

  const rejectedNote = <RejectedFilesNote names={rejectedNames} />;

  const hiddenFileInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      hidden
      onChange={handleFilesPicked}
      data-test-id="composer-file-input"
    />
  );

  if (multiline) {
    return (
      <Box>
        <Box
          data-mention-input-container
          bg="base-white"
          border="1px solid"
          borderColor="border-color"
          borderRadius="12px"
          px="10px"
          py="12px"
        >
          {rejectedNote}
          {slashPopover}
          <MentionEditor
            ref={editorRef}
            multiline
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={56}
            maxHeight={160}
            testId="composer-input"
          />
          <Flex align="center" justify="space-between" gap="8px" mt="6px">
            <Box flex="1" minW="0">
              <AttachmentChips files={files} onRemove={removeFile} />
            </Box>
            <ComposerActions
              canSend={canSend}
              disabled={isAwaiting}
              onSend={handleSend}
              onAttach={openPicker}
            />
          </Flex>
        </Box>
        {hiddenFileInput}
      </Box>
    );
  }

  return (
    <Box>
      {files.length > 0 && (
        <Box mb="8px">
          <AttachmentChips files={files} onRemove={removeFile} />
        </Box>
      )}
      {rejectedNote}
      {slashPopover}
      <Flex
        data-mention-input-container
        align="center"
        gap="8px"
        bg="base-white"
        border="1px solid"
        borderColor="border-color"
        borderRadius="12px"
        px="12px"
        py="8px"
      >
        <Box flex="1" minW="0">
          <MentionEditor
            ref={editorRef}
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={24}
            testId="composer-input"
          />
        </Box>
        <ComposerActions
          canSend={canSend}
          disabled={isAwaiting}
          onSend={handleSend}
          onAttach={openPicker}
          awaiting={isAwaiting}
          onStop={() => void stopTurn()}
          stopping={stopping}
        />
      </Flex>
      {hiddenFileInput}
      <Text className="text-center mt-2 text-secondary-400 font-[450] text-[12px]">
        {DISCLAIMER}
      </Text>
    </Box>
  );
};
